import cx from 'classnames';
import { ReactElement, useState } from 'react';

import { CmsComponentSwitch } from 'src/cms/CmsComponentSwitch';
import { Pagination } from 'src/components/Blog';
import { ContentfulToolbar } from 'src/components/ContentfulToolbar';
import { createSearchDropdown } from 'src/components/Inputs/SearchBar';
import {
  Tile,
  TileGroup,
  articleTileGroupAdapter,
} from 'src/components/Layout';
import {
  CmsThemes,
  ContentfulTemplates,
  Locales,
  RESOURCES_PATH,
  type TLocale,
} from 'src/lib/constants';
import { renderCtaBlock, searchBasePath } from 'src/lib/resources-utils';
import { TemplateWrapper } from 'src/templates';

import styles from './TopicPageTemplate.module.scss';

const TopicPageTemplate = ({
  fields,
  articles,
  topic,
  totalArticles,
  pageNumber,
  topics = [],
  locale = Locales.EN_US,
  ...props
}: {
  fields?;
  articles?;
  topic?;
  totalArticles?;
  pageNumber?;
  topics?;
  locale?: TLocale;
}): ReactElement => {
  const numberOfPages = Math.ceil(totalArticles / 9);

  const currentTopic = fields?.topic;
  // URL/search paths use topic pageSlug (matches getStaticPaths), not the
  // display label in fields.topic.
  const [searchTopic, setSearchTopic] = useState(topic);

  // creates an object of all the topics ex. {ach:{topic:'ACH'}, api:{topic:'API'}, 'all topics': {topic:{..}}, banking:{topic:'Banking'}}
  const topicsObj = topics.reduce((obj, topicOption) => {
    if (topicOption) {
      const topicProp = topicOption.value || topicOption.label || topicOption;
      return {
        ...obj,
        [String(topicProp).toLowerCase()]: {
          topic: topicOption,
        },
      };
    }

    return obj;
  }, {});

  // Arranges the topics in alphabetical order based on the keys of the topicsObj ex. ['ACH',{..},'API','Banking']
  const sortedTopics = Object.keys(topicsObj)
    .sort()
    .map((e) => {
      return topicsObj[e].topic;
    });
  topics = sortedTopics;

  // eslint-disable-next-line no-console
  const handleChange = (search) => {
    search?.selectedOption && setSearchTopic(search.selectedOption);
  };

  const topicsDropdown = createSearchDropdown({
    label: 'Topics',
    options: topics,
    defaultOption: currentTopic || '',
  });

  const search = {
    placeholder: 'Search',
    destination: searchBasePath({ topic: searchTopic, locale }),
    resultType: 'resource',
    onChange: handleChange,
    label: `${totalArticles + 1} resource${
      totalArticles !== 1 ? 's' : ''
    } available`, // ex: 2 resources available
    dropdowns: [topicsDropdown],
  };

  const renderArticleTiles = () => {
    try {
      const { tileGroup, ...tileGroupProps } = articleTileGroupAdapter({
        articles,
      });

      return (
        <>
          <TileGroup {...tileGroupProps}>
            {tileGroup.map((tile, idx) => {
              return <Tile key={`article-tile-${idx}`} {...tile} />;
            })}
          </TileGroup>
          {totalArticles > 9 && (
            <div className={cx('grid-container', styles.paginationContainer)}>
              <Pagination
                currentPage={pageNumber || 1}
                totalPages={numberOfPages}
                basePath={`${RESOURCES_PATH}/${topic}`}
              />
            </div>
          )}
        </>
      );
    } catch (e) {
      throw new Error(e);
    }
  };

  return (
    <TemplateWrapper
      meta-title={fields?.metaTitle}
      meta-description={fields?.metaDescription}
      hasFooterCta={fields?.hasFooterCta}
      noIndex={fields?.metaNoindex}
      className={styles.topicPage}
      openGraphImage={fields?.openGraphImage?.fields?.file?.url}
      cmsTheme={CmsThemes.WEB3}
      locale={locale}
    >
      <div data-testid={ContentfulTemplates.TOPIC}>
        <CmsComponentSwitch
          components={fields?.components}
          search={search}
          topic={currentTopic}
          templatePage={ContentfulTemplates.TOPIC}
        />
        {renderArticleTiles()}
        {renderCtaBlock(fields)}
      </div>
      <ContentfulToolbar />
    </TemplateWrapper>
  );
};

export default TopicPageTemplate;
